import { useCallback } from "react";
import { FEATURE_ADDON_KEYS } from "../actions/featureAddons";
import { PRODUCE_MAX_ROWS } from "../features/creativeTemplates/produce/produceRows";
import useFeatureAddonPaywall from "./useFeatureAddonPaywall";

const resolveProduceLimit = ({ status, hardMax }) => {
  const raw = Number(status?.effective?.produceLimit);
  if (Number.isFinite(raw) && raw > 0) return raw;
  return hardMax;
};

/**
 * Templates addon gates: saved-template capacity and pieces per produce run.
 */
const useTemplatesAddon = () => {
  const paywall = useFeatureAddonPaywall({
    addonKey: FEATURE_ADDON_KEYS.TEMPLATES,
  });

  const blockIfCapacityReached = useCallback(
    ({ liveCount }) =>
      paywall.blockWhen((status) => {
        const limit = Number(status?.effective?.limit);
        return (
          status?.ok && Number.isFinite(limit) && Number(liveCount) >= limit
        );
      }),
    [paywall.blockWhen]
  );

  const blockIfProduceBatchExceeded = useCallback(
    async ({ requestedCount, hardMax = PRODUCE_MAX_ROWS }) => {
      const count = Number(requestedCount) || 0;
      if (!paywall.eligible) {
        return {
          blocked: count > hardMax,
          paywallOpened: false,
          limit: hardMax,
        };
      }
      const status = await paywall.refreshStatus();
      const limit = resolveProduceLimit({ status, hardMax });
      if (count <= limit) {
        return { blocked: false, paywallOpened: false, limit };
      }
      if (limit < hardMax) {
        paywall.openPaywall();
        return { blocked: true, paywallOpened: true, limit };
      }
      return { blocked: true, paywallOpened: false, limit };
    },
    [paywall.eligible, paywall.openPaywall, paywall.refreshStatus]
  );

  return {
    ...paywall,
    blockIfCapacityReached,
    blockIfProduceBatchExceeded,
  };
};

export default useTemplatesAddon;
